import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, RefreshCw, Search, Calendar, RotateCcw } from 'lucide-react';
import { Card, Button, Input, EmptyState } from '../ui';
import { projectsApi, ProjectDto } from '../../services';
import './ArchivedProjects.css';

interface ArchivedProjectsProps {
  onRestore?: (project: ProjectDto) => void;
}

export const ArchivedProjects: React.FC<ArchivedProjectsProps> = ({
  onRestore,
}) => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<ProjectDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const fetchArchivedProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await projectsApi.getArchivedProjects();
      setProjects(data);
      setFilteredProjects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load archived projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArchivedProjects();
  }, [fetchArchivedProjects]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProjects(projects);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredProjects(
        projects.filter(
          p =>
            p.title.toLowerCase().includes(query) ||
            p.projectType.toLowerCase().includes(query) ||
            (p.description && p.description.toLowerCase().includes(query))
        )
      );
    }
  }, [searchQuery, projects]);

  const handleRestore = useCallback(
    async (project: ProjectDto) => {
      if (!window.confirm(`Are you sure you want to restore "${project.title}"?`)) {
        return;
      }

      setRestoringId(project.id);
      try {
        const restored = await projectsApi.unarchiveProject(project.id);
        setProjects(prev => prev.filter(p => p.id !== project.id));
        onRestore?.(restored);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to restore project');
      } finally {
        setRestoringId(null);
      }
    },
    [onRestore]
  );

  if (loading) {
    return (
      <Card className="archived-projects archived-projects--loading">
        <div className="archived-projects__spinner" />
        <p>Loading archived projects...</p>
      </Card>
    );
  }

  return (
    <div className="archived-projects">
      <div className="archived-projects__header">
        <div className="archived-projects__title-section">
          <h2 className="archived-projects__title">
            <Archive size={24} />
            Archived Projects
          </h2>
          <span className="archived-projects__count">
            {projects.length} {projects.length === 1 ? 'project' : 'projects'}
          </span>
        </div>

        <div className="archived-projects__actions">
          <div className="archived-projects__search">
            <Search size={16} className="archived-projects__search-icon" />
            <Input
              type="text"
              placeholder={t('archived.searchPlaceholder')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="archived-projects__search-input"
            />
          </div>
          <Button variant="ghost" onClick={fetchArchivedProjects} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="archived-projects__error">
          {error}
          <Button variant="ghost" size="sm" onClick={fetchArchivedProjects}>
            Try again
          </Button>
        </div>
      )}

      {filteredProjects.length === 0 ? (
        <EmptyState
          variant={searchQuery ? 'no-search-results' : 'no-archived'}
          title={searchQuery ? 'No projects found' : 'No archived projects yet'}
          message={
            searchQuery
              ? 'Try adjusting your search. Your projects are still here, just hidden from view.'
              : 'Completed projects will appear here when you archive them. Take your time with your current work.'
          }
        />
      ) : (
        <div className="archived-projects__grid">
          {filteredProjects.map(project => (
            <Card key={project.id} className="archived-projects__card">
              <div className="archived-projects__card-header">
                <span className="archived-projects__card-type">
                  {project.projectType}
                </span>
                <span className="archived-projects__card-stage">
                  {project.currentStage}
                </span>
              </div>

              <h3 className="archived-projects__card-title">{project.title}</h3>

              {project.description && (
                <p className="archived-projects__card-desc">
                  {project.description.length > 100
                    ? `${project.description.substring(0, 100)}...`
                    : project.description}
                </p>
              )}

              <div className="archived-projects__card-meta">
                <div className="archived-projects__card-date">
                  <Calendar size={14} />
                  <span>
                    Archived{' '}
                    {project.updatedAt
                      ? new Date(project.updatedAt).toLocaleDateString()
                      : 'Unknown date'}
                  </span>
                </div>
              </div>

              <div className="archived-projects__card-actions">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleRestore(project)}
                  disabled={restoringId === project.id}
                >
                  {restoringId === project.id ? (
                    <>
                      <RefreshCw size={14} className="spinning" />
                      Restoring...
                    </>
                  ) : (
                    <>
                      <RotateCcw size={14} />
                      Restore
                    </>
                  )}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ArchivedProjects;
